<template>
  <div class="proposals-list">
    <div class="header">
      <button @click="$emit('back')" class="back-btn">
        ← Back to Companies
      </button>
      
      <div class="tenant-info">
        <h2>📋 Proposals for {{ tenant.name }}</h2>
        <p>Review and manage AI-generated schedule proposals</p>
      </div>
      
      <button @click="$emit('generate-new', tenant)" class="generate-new-btn">
        🤖 Generate New Schedule
      </button>
    </div>

    <!-- Quick Stats -->
    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-number">{{ totalProposals }}</div>
        <div class="stat-label">Total Proposals</div>
      </div>
      
      <div class="stat-card">
        <div class="stat-number">{{ pendingCount }}</div>
        <div class="stat-label">Pending Review</div>
      </div>
      
      <div class="stat-card">
        <div class="stat-number">{{ approvedCount }}</div>
        <div class="stat-label">Approved</div>
      </div>
      
      <div class="stat-card">
        <div class="stat-number">{{ avgConfidence }}%</div>
        <div class="stat-label">Avg Confidence</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <select v-model="statusFilter" @change="handleFilterChange" class="filter-select">
        <option value="">All Status</option>
        <option value="pending">Pending</option>
        <option value="reviewing">Under Review</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
        <option value="committed">Deployed</option>
      </select>
      
      <input
        v-model="startDateFilter"
        type="date"
        placeholder="Start Date"
        class="filter-input"
      >
      
      <input
        v-model="endDateFilter"
        type="date"
        placeholder="End Date"
        class="filter-input"
      >
      
      <button @click="refreshProposals" :disabled="loading" class="refresh-btn">
        {{ loading ? '🔄' : '↻' }} Refresh
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading proposals...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h4>Failed to load proposals</h4>
      <p>{{ error }}</p>
      <button @click="refreshProposals" class="retry-btn">Try Again</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredProposals.length === 0" class="empty-state">
      <div class="empty-icon">📋</div>
      <h4>No proposals found</h4>
      <p v-if="hasFilters">Try adjusting your filters</p>
      <p v-else>No schedule proposals have been generated yet</p>
      <button @click="$emit('generate-new', tenant)" class="generate-first-btn">
        🤖 Generate First Proposal
      </button>
    </div>

    <!-- Proposals List -->
    <div v-else class="proposals-grid">
      <div
        v-for="proposal in filteredProposals"
        :key="proposal.id"
        @click="reviewProposal(proposal)"
        class="proposal-card"
        :class="{ 
          'pending': proposal.status === 'pending',
          'approved': proposal.status === 'approved',
          'rejected': proposal.status === 'rejected'
        }"
      >
        <div class="proposal-header">
          <div class="proposal-id">
            Proposal #{{ proposal.id.slice(-8) }}
          </div>
          <div class="proposal-status">
            <span class="status-badge" :class="proposal.status">
              {{ getStatusLabel(proposal.status) }}
            </span>
          </div>
        </div>

        <div class="proposal-period">
          <span class="period-icon">📅</span>
          <span class="period-text">
            {{ formatDate(proposal.schedule_start_date) }} - {{ formatDate(proposal.schedule_end_date) }}
          </span>
        </div>

        <div class="proposal-stats">
          <div class="stat-item">
            <span class="stat-icon">👥</span>
            <span class="stat-text">{{ proposal.employee_count || 0 }} employees</span>
          </div>
          <div class="stat-item">
            <span class="stat-icon">⏰</span>
            <span class="stat-text">{{ proposal.shift_count || 0 }} shifts</span>
          </div>
          <div class="stat-item">
            <span class="stat-icon">🎯</span>
            <span class="stat-text">{{ Math.round((proposal.confidence_score || 0) * 100) }}% confidence</span>
          </div>
        </div>

        <div class="proposal-method" v-if="proposal.generated_by">
          <span class="method-label">Method:</span>
          <span class="method-value">{{ getMethodLabel(proposal.generated_by) }}</span>
        </div>

        <div class="proposal-created">
          <span class="created-icon">🕒</span>
          <span class="created-text">{{ formatRelativeTime(proposal.created_at) }}</span>
        </div>

        <div class="proposal-summary" v-if="proposal.summary">
          <p>{{ truncateText(proposal.summary, 100) }}</p>
        </div>

        <div class="proposal-actions">
          <button
            @click.stop="reviewProposal(proposal)"
            class="review-btn"
          >
            👁️ Review
          </button>
          
          <button
            v-if="proposal.status === 'pending'"
            @click.stop="quickApprove(proposal)"
            class="approve-btn"
          >
            ✅ Quick Approve
          </button>
          
          <button
            v-if="proposal.status === 'pending'"
            @click.stop="quickReject(proposal)"
            class="reject-btn"
          >
            ❌ Reject
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination (placeholder) -->
    <div v-if="totalProposals > filteredProposals.length" class="pagination">
      <button class="load-more-btn">Load More Proposals</button>
    </div>

    <!-- Rejection Modal -->
    <RejectionModal 
      ref="rejectionModal"
      @confirm="handleRejectionConfirmed"
      @cancel="handleRejectionCancelled"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAISchedulerStore } from '../../stores/aiScheduler.js'
import RejectionModal from './RejectionModal.vue'

const props = defineProps({
  tenant: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['back', 'review-proposal', 'generate-new'])

const aiSchedulerStore = useAISchedulerStore()

// Filters
const statusFilter = ref('')
const startDateFilter = ref('')
const endDateFilter = ref('')

// Modal ref
const rejectionModal = ref(null)
const proposalToReject = ref(null)

// Computed properties
const loading = computed(() => aiSchedulerStore.proposalsLoading)
const error = computed(() => aiSchedulerStore.proposalsError)
const proposals = computed(() => aiSchedulerStore.proposals)

const filteredProposals = computed(() => {
  let filtered = [...proposals.value]
  
  if (statusFilter.value) {
    filtered = filtered.filter(p => p.status === statusFilter.value)
  }
  
  if (startDateFilter.value) {
    filtered = filtered.filter(p => p.schedule_start_date >= startDateFilter.value)
  }
  
  if (endDateFilter.value) {
    filtered = filtered.filter(p => p.schedule_end_date <= endDateFilter.value)
  }
  
  return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})

const totalProposals = computed(() => proposals.value.length)
const pendingCount = computed(() => proposals.value.filter(p => p.status === 'pending').length)
const approvedCount = computed(() => proposals.value.filter(p => p.status === 'approved').length)

const avgConfidence = computed(() => {
  const scores = proposals.value.map(p => p.confidence_score || 0)
  return scores.length > 0 
    ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length * 100)
    : 0
})

const hasFilters = computed(() => {
  return statusFilter.value || startDateFilter.value || endDateFilter.value
})

// Methods
const refreshProposals = async () => {
  try {
    await aiSchedulerStore.loadProposals({
      tenant_id: props.tenant.schema_name || props.tenant.id,
      status: statusFilter.value || undefined,
      start_date: startDateFilter.value || undefined,
      end_date: endDateFilter.value || undefined
    })
  } catch (error) {
    console.error('Failed to refresh proposals:', error)
  }
}

const handleFilterChange = () => {
  refreshProposals()
}

const reviewProposal = async (proposal) => {
  try {
    // Fetch full proposal details including proposal_data
    
    // Import the API service
    const aiSchedulerApi = (await import('../../services/aiSchedulerApi.js')).default
    const fullProposal = await aiSchedulerApi.getProposal(proposal.id)
    
    emit('review-proposal', fullProposal)
  } catch (error) {
    // Fallback to partial data
    emit('review-proposal', proposal)
  }
}

const quickApprove = async (proposal) => {
  try {
    await aiSchedulerStore.approveProposal(
      proposal.id,
      'Quick approved via proposals list'
    )
    await refreshProposals()
  } catch (error) {
    console.error('Failed to approve proposal:', error)
  }
}

const quickReject = async (proposal) => {
  proposalToReject.value = proposal
  rejectionModal.value.show()
}

const handleRejectionConfirmed = async (reason) => {
  if (!proposalToReject.value) return
  
  try {
    await aiSchedulerStore.rejectProposal(proposalToReject.value.id, reason)
    await refreshProposals()
  } catch (error) {
    console.error('Failed to reject proposal:', error)
  } finally {
    proposalToReject.value = null
  }
}

const handleRejectionCancelled = () => {
  proposalToReject.value = null
}

const getStatusLabel = (status) => {
  const labels = {
    'pending': 'Pending Review',
    'reviewing': 'Under Review',
    'approved': 'Approved',
    'rejected': 'Rejected',
    'committed': 'Deployed'
  }
  return labels[status] || status
}

const getMethodLabel = (method) => {
  const labels = {
    'claude': 'Claude AI',
    'ml_model': 'ML Model',
    'rule_engine': 'Rule Engine'
  }
  return labels[method] || method
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  }).format(date)
}

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'Unknown'
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now - date
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Lifecycle
onMounted(async () => {
  await refreshProposals()
})

// Watch for tenant changes
watch(() => props.tenant, async () => {
  if (props.tenant) {
    await refreshProposals()
  }
}, { immediate: true })
</script>

<style scoped>
/* Add comprehensive styles similar to other components */
.proposals-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.back-btn, .generate-new-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.back-btn {
  background: #f3f4f6;
  color: #374151;
}

.back-btn:hover {
  background: #e5e7eb;
}

.generate-new-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.generate-new-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.tenant-info h2 {
  margin: 0;
  color: #1a1a1a;
  font-size: 1.5rem;
  font-weight: 600;
}

.tenant-info p {
  margin: 0.25rem 0 0 0;
  color: #6b7280;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.filters-section {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-select, .filter-input {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.refresh-btn {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.loading-state, .error-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 2rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.error-icon, .empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.generate-first-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  margin-top: 1rem;
}

.proposals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 4rem;
}

.proposal-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.proposal-card:hover {
  border-color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.15);
}

.proposal-card.pending {
  border-left: 4px solid #f59e0b;
}

.proposal-card.approved {
  border-left: 4px solid #10b981;
}

.proposal-card.rejected {
  border-left: 4px solid #ef4444;
}

.proposal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.proposal-id {
  font-weight: 600;
  color: #1a1a1a;
  font-family: monospace;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.approved {
  background: #dcfce7;
  color: #166534;
}

.status-badge.rejected {
  background: #fef2f2;
  color: #991b1b;
}

.proposal-period {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.proposal-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.proposal-method {
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.method-label {
  color: #6b7280;
}

.method-value {
  color: #1a1a1a;
  font-weight: 500;
}

.proposal-created {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #9ca3af;
  font-size: 0.75rem;
}

.proposal-summary p {
  margin: 0 0 1rem 0;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
}

.proposal-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.review-btn, .approve-btn, .reject-btn {
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s;
}

.review-btn {
  background: #f3f4f6;
  color: #374151;
  flex: 1;
}

.approve-btn {
  background: #dcfce7;
  color: #166534;
}

.reject-btn {
  background: #fef2f2;
  color: #991b1b;
}

.review-btn:hover {
  background: #e5e7eb;
}

.approve-btn:hover {
  background: #bbf7d0;
}

.reject-btn:hover {
  background: #fecaca;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .proposals-list {
    padding: 1rem;
  }
  
  .header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .proposals-grid {
    grid-template-columns: 1fr;
  }
  
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>